import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./bookEvents.css";
import BeforeFooter from "../components/layout/BeforeFooter";
import { EVENTS, EVENTS_PATH, getSeats, reserveSeat } from "../data/events";

/* ============================================================
   TICKET OVERLAY
   ============================================================ */
function TicketOverlay({ info, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="events-overlay" onClick={onClose}>
      <div
        className="events-overlay-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="events-overlay-title"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="events-overlay-icon" aria-hidden="true">✓</span>
        <h3 id="events-overlay-title">Your ticket is on its way</h3>
        <p>
          Thank you, {info.name}. We sent your ticket to <strong>{info.email}</strong>.
          Please check your spam folder if you don't see it.
        </p>
        <p className="events-overlay-date">See you on {info.date}.</p>
        <button ref={closeRef} type="button" className="events-ig-btn" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   FAIR PAGE
   ============================================================ */
function FairPage({ event }) {
  const f = event.fair;
  const covers = event.covers ?? [];
  const place = `${event.venue}${event.area ? `, ${event.area}` : ""}`;

  return (
    <>
      <section className="events-hero">
        <div className="events-hero-text">
          <Link to={EVENTS_PATH} className="events-back">← All events</Link>
          <p className="events-eyebrow">{event.date}</p>
          <h1>{event.name}</h1>
          <p className="events-hero-sub">{event.tagline}</p>
          <ul className="events-facts">
            <li><strong>Hours</strong> {event.time}</li>
            <li>
              <strong>Where</strong>{" "}
              {event.mapUrl ? (
                <a href={event.mapUrl} target="_blank" rel="noopener noreferrer" className="events-map-link">
                  {place} <span aria-hidden="true">↗</span>
                </a>
              ) : (
                place
              )}
            </li>
            <li><strong>Entry</strong> {event.entry}</li>
          </ul>
        </div>
        <div className="events-hero-img">
          {covers.length > 0 ? (
            <div className={`events-covers events-covers--${Math.min(covers.length, 2)}`}>
              {covers.slice(0, 2).map((c, i) => (
                <img
                  key={i}
                  src={c.src}
                  alt={c.alt}
                  className={`events-cover ${i === 0 ? "events-cover--left" : "events-cover--right"}`}
                />
              ))}
            </div>
          ) : (
            <div className="events-hero-placeholder">🎟️</div>
          )}
        </div>
      </section>

      <section className="events-detail">
        <div className="events-detail-grid">
          <div className="events-programme" data-aos="fade-up">
            <h2>At the stand</h2>
            <ul className="events-programme-list events-fair-list">
              {f.activities.map((a) => (
                <li key={a.title}>
                  {a.image !== undefined &&
                    (a.image ? (
                      <img src={a.image} alt={a.imageAlt} className="events-activity-img" loading="lazy" />
                    ) : (
                      <div className="events-activity-placeholder" aria-hidden="true">📷</div>
                    ))}
                  <span>
                    <strong>{a.title}</strong>
                    <br />
                    {a.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="events-reserve" data-aos="fade-up" data-aos-delay="80">
            <h2>Find the stand</h2>
            <p className="events-seats">
              <span dir="auto">{f.hall}</span> · {f.stand}
            </p>
            <p className="events-note">
              <strong>Signing session</strong><br />
              {f.signing.day}, {f.signing.time}
            </p>
            <p className="events-note">The book is at the stand every day of the fair.</p>
            {f.bookUrl && (
              <p>
                <Link to={f.bookUrl} className="events-ig-btn">Explore the book →</Link>
              </p>
            )}
          </div>
        </div>

        {f.offer && (
          <div className="events-offer" data-aos="fade-up">
            <h2>{f.offer.title}</h2>
            <p className="events-note">{f.offer.note}</p>
            <ul className="events-offer-items">
              {f.offer.items.map((it) => <li key={it}>{it}</li>)}
            </ul>
            <p className="events-offer-price">{f.offer.price}</p>
          </div>
        )}

        {f.gallery?.length > 0 && (
          <div className="events-wall" data-aos="fade-up">
            <h2>A peek at the stand</h2>
            <div className="events-gallery">
              {f.gallery.map((p, i) => (
                <img key={i} src={p.src} alt={p.alt} loading="lazy" />
              ))}
            </div>
          </div>
        )}
      </section>

      <BeforeFooter />
    </>
  );
}

/* ============================================================
   RESERVATION FORM
   ============================================================ */
const EMPTY_FORM = { name: "", phone: "", email: "", seats: "1", updates: false, _gotcha: "" };

function ReservationForm({ event, left, onBooked }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | sending
  const [error, setError] = useState("");
  const maxSeats = Math.max(1, Math.min(event.maxPerBooking, left ?? event.maxPerBooking));

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form._gotcha) return; // spam trap
    if (form.name.trim().length < 2) return setError("Please enter your name.");
    if (form.phone.replace(/\D/g, "").length < 8) return setError("Please enter a phone number we can reach you on.");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return setError("Please enter a valid email so we can send your ticket.");
    setError("");
    setStatus("sending");

    let data = null;
    try {
      data = await reserveSeat(event, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        seats: Number(form.seats),
        updates: form.updates,
      });
    } catch {
      data = null;
    }

    setStatus("idle");
    if (!data) return setError("Your reservation did not go through. Please try again.");

    if (data.ok) {
      const info = {
        name: form.name.trim().split(" ")[0],
        email: form.email.trim(),
        date: event.date,
      };
      setForm(EMPTY_FORM); // form goes back to normal
      onBooked(info);      // parent shows the overlay and refreshes the counter
      return;
    }

    onBooked(null); // refresh the counter only
    if (data.reason === "duplicate") setError("This phone number already has a reservation.");
    else if (data.reason === "full") setError(data.left > 0 ? `Only ${data.left} seat(s) left. Please lower the number of seats.` : "Sorry, all seats were just taken.");
    else if (data.reason === "email") setError("We could not send your ticket, so no seat was reserved. Please check your email address and try again.");
    else setError("Please check your name, phone number and email.");
  };

  return (
    <form className="events-form" onSubmit={submit} noValidate>
      <label>
        Full name
        <input name="name" value={form.name} onChange={update} autoComplete="name" required />
      </label>
      <label>
        Phone number
        <input name="phone" type="tel" value={form.phone} onChange={update} autoComplete="tel" required />
      </label>
      <label>
        Email <span className="events-optional">(your ticket will be sent here)</span>
        <input name="email" type="email" value={form.email} onChange={update} autoComplete="email" required />
      </label>
      <label>
        Number of seats
        <select name="seats" value={Math.min(Number(form.seats), maxSeats)} onChange={update}>
          {Array.from({ length: maxSeats }, (_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1}</option>
          ))}
        </select>
      </label>
      <label className="events-check">
        <input name="updates" type="checkbox" checked={form.updates} onChange={update} />
        Send me news about future events
      </label>
      {/* honeypot: hidden from people, filled by bots */}
      <input name="_gotcha" value={form._gotcha} onChange={update} className="events-hp" tabIndex="-1" autoComplete="off" aria-hidden="true" />
      {error && <p className="events-form-error" role="alert">{error}</p>}
      <button type="submit" className="events-ig-btn" disabled={status === "sending"}>
        {status === "sending" ? "Reserving..." : "Reserve my seat"}
      </button>
      <p className="events-privacy">We only use your email to send your ticket.</p>
    </form>
  );
}

/* ============================================================
   EVENT PAGE
   ============================================================ */
function EventDetail() {
  const { eventId } = useParams();
  const event = EVENTS.find((e) => e.id === eventId && e.status !== "hidden");
  const [seats, setSeats] = useState(null); // { limit, taken } or null while loading
  const [ticketInfo, setTicketInfo] = useState(null); // overlay content

  const isPast = event?.status === "past";

  const loadSeats = async () => {
    if (!event || isPast || !event.registrationOpen) return;
    try {
      setSeats(await getSeats(event));
    } catch {
      /* counter stays hidden if the server is unreachable */
    }
  };

  const handleBooked = (info) => {
    if (info) setTicketInfo(info);
    loadSeats();
  };

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    loadSeats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // 1) Unknown or hidden event
  if (!event) {
    return (
      <>
        <section className="events-hero">
          <div className="events-hero-text">
            <Link to={EVENTS_PATH} className="events-back">← All events</Link>
            <h1>Event not found</h1>
            <p className="events-hero-sub">This event does not exist or is not public yet.</p>
          </div>
        </section>
        <BeforeFooter />
      </>
    );
  }

  // 2) Upcoming fair: its own layout, no reservation form
  if (event.type === "fair" && !isPast) {
    return <FairPage event={event} />;
  }

  const left = seats ? Math.max(seats.limit - seats.taken, 0) : null;

  let reserve;
  if (!event.registrationOpen) {
    reserve = <p className="events-seats">Reservations open soon. Check back on this page.</p>;
  } else if (left === 0) {
    reserve = (
      <div className="events-form-done" role="status">
        <h3>All seats are taken</h3>
        <p>Thank you for your interest. Photos and news will be shared on this page after the event.</p>
      </div>
    );
  } else {
    reserve = (
      <>
        <p className="events-seats">
          {left === null ? `Limited to ${event.limit} seats.` : `${left} of ${event.limit} seats left`}
        </p>
        <ReservationForm event={event} left={left} onBooked={handleBooked} />
      </>
    );
  }

  const covers = event.covers ?? [];
  const place = `${event.venue}${event.area ? `, ${event.area}` : ""}`;

  return (
    <>
      {/* HERO */}
      <section className="events-hero">
        <div className="events-hero-text">
          <Link to={EVENTS_PATH} className="events-back">← All events</Link>
          <p className="events-eyebrow">{isPast ? `${event.date} · Past event` : event.date}</p>
          <h1>{event.name}</h1>
          <p className="events-hero-sub">{event.tagline}</p>
          <ul className="events-facts">
            <li><strong>When</strong> {event.time}</li>
            <li>
              <strong>Where</strong>{" "}
              {event.mapUrl ? (
                <a
                  href={event.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="events-map-link"
                >
                  {place} <span aria-hidden="true">↗</span>
                  <span className="events-sr-only"> (opens in Google Maps)</span>
                </a>
              ) : (
                place
              )}
            </li>
            <li><strong>Entry</strong> Free</li>
          </ul>
        </div>
        <div className="events-hero-img">
          {covers.length > 0 ? (
            <div className={`events-covers events-covers--${Math.min(covers.length, 2)}`}>
              {covers.slice(0, 2).map((c, i) => (
                <img
                  key={i}
                  src={c.src}
                  alt={c.alt}
                  className={`events-cover ${i === 0 ? "events-cover--left" : "events-cover--right"}`}
                />
              ))}
            </div>
          ) : (
            <div className="events-hero-placeholder">🎟️</div>
          )}
        </div>
      </section>

      {isPast ? (
        /* PAST EVENT: recap and photos */
        <section className="events-past-page">
          {event.recap && <p className="events-past-recap">{event.recap}</p>}
          {event.photos.length > 0 && (
            <div className="events-gallery">
              {event.photos.map((photo, i) => (
                <img key={i} src={photo.src} alt={photo.alt} loading="lazy" />
              ))}
            </div>
          )}
        </section>
      ) : (
        /* UPCOMING EVENT: programme and reservation */
        <section className="events-detail">
          <div className="events-detail-grid">
            <div className="events-programme" data-aos="fade-up">
              <h2>The evening</h2>
              <ol className="events-programme-list">
                {event.programme.map((p) => (
                  <li key={p.time}>
                    <span className="events-time">{p.time}</span>
                    <span>
                      <strong>{p.title}</strong>
                      <br />
                      {p.text}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="events-note">{event.note}</p>
            </div>

            <div className="events-reserve" data-aos="fade-up" data-aos-delay="80" id="reserve">
              <h2>Reserve your seat</h2>
              {reserve}
            </div>
          </div>
        </section>
      )}

      {ticketInfo && <TicketOverlay info={ticketInfo} onClose={() => setTicketInfo(null)} />}

      <BeforeFooter />
    </>
  );
}

export default EventDetail;